import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';
import umbrella from 'umbrella-storage';
import { ApiEnvelope } from '../../models/v2';

const configuredBase = process.env.REACT_APP_BASE_API;
const serviceBase =
    configuredBase ||
    (process.env.NODE_ENV === 'production'
        ? 'https://www.heartyconsulting.com/crmservice'
        : 'http://localhost:8080/crmservice');

const snakeToCamel = (value: string) =>
    value.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

const normalizeKeys = (value: any): any => {
    if (Array.isArray(value)) return value.map(normalizeKeys);
    if (value && Object.prototype.toString.call(value) === '[object Object]') {
        return Object.keys(value).reduce((result: any, key: string) => {
            result[snakeToCamel(key)] = normalizeKeys(value[key]);
            return result;
        }, {});
    }
    return value;
};

export class V2ApiError extends Error {
    code: string;
    requestId?: string;
    status?: number;
    details?: any;

    constructor(
        message: string,
        code = 'UNKNOWN_ERROR',
        requestId?: string,
        status?: number,
        details?: any
    ) {
        super(message);
        this.name = 'V2ApiError';
        this.code = code;
        this.requestId = requestId;
        this.status = status;
        this.details = details;
    }
}

const createIdempotencyKey = () => {
    const random = Math.random().toString(16).slice(2);
    return `web-${Date.now().toString(36)}-${random}`;
};

class V2Client {
    private http: AxiosInstance;

    constructor() {
        this.http = axios.create({
            baseURL: `${serviceBase.replace(/\/$/, '')}/api/v2`,
            timeout: 20000,
            withCredentials: true,
            xsrfCookieName: 'XSRF-TOKEN',
            xsrfHeaderName: 'X-CSRF-Token',
        });

        this.http.interceptors.request.use((config) => {
            const token = Cookies.get('token');
            if (token) config.headers.Authorization = `Bearer ${token}`;
            const csrf = Cookies.get('XSRF-TOKEN') || Cookies.get('csrf_token');
            if (csrf && config.method && config.method.toLowerCase() !== 'get') {
                config.headers['X-CSRF-Token'] = csrf;
            }
            return config;
        });
    }

    private async request<T>(config: AxiosRequestConfig, idempotent = false): Promise<T> {
        const nextConfig = { ...config, headers: { ...(config.headers || {}) } };
        if (idempotent) nextConfig.headers!['Idempotency-Key'] = createIdempotencyKey();
        try {
            const response = await this.http.request<ApiEnvelope<T>>(nextConfig);
            const envelope = normalizeKeys(response.data) as ApiEnvelope<T>;
            if (!envelope.success) {
                throw new V2ApiError(
                    envelope.message,
                    envelope.code,
                    envelope.requestId,
                    response.status,
                    envelope.data
                );
            }
            return envelope.data;
        } catch (error) {
            if (error instanceof V2ApiError) throw error;
            const axiosError = error as AxiosError<ApiEnvelope<any>>;
            const body = axiosError.response && normalizeKeys(axiosError.response.data);
            if (axiosError.response && axiosError.response.status === 401) {
                umbrella.removeLocalStorage('user');
                Cookies.remove('token', { path: '/' });
                Cookies.remove('userRole', { path: '/' });
                if (!window.location.hash.startsWith('#/login')) window.location.href = '/#/login';
            }
            throw new V2ApiError(
                (body && body.message) ||
                    (axiosError.response ? '请求未成功，请稍后重试' : '无法连接服务器'),
                (body && body.code) || 'NETWORK_ERROR',
                body && body.requestId,
                axiosError.response && axiosError.response.status,
                body && body.data
            );
        }
    }

    get<T>(url: string, params?: any) {
        return this.request<T>({ method: 'GET', url, params });
    }

    post<T>(url: string, data?: any, idempotent = true) {
        return this.request<T>({ method: 'POST', url, data }, idempotent);
    }

    postLong<T>(url: string, data?: any) {
        return this.request<T>({ method: 'POST', url, data, timeout: 120000 }, true);
    }

    postMultipart<T>(url: string, data: FormData, idempotent = false) {
        return this.request<T>({ method: 'POST', url, data, timeout: 60000 }, idempotent);
    }

    postForm<T>(url: string, data: URLSearchParams) {
        return this.request<T>({
            method: 'POST',
            url,
            data,
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        });
    }

    patch<T>(url: string, data?: any) {
        return this.request<T>({ method: 'PATCH', url, data }, true);
    }

    delete<T>(url: string, data?: any) {
        return this.request<T>({ method: 'DELETE', url, data }, true);
    }

    put<T>(url: string, data?: any) {
        return this.request<T>({ method: 'PUT', url, data }, true);
    }
}

export default new V2Client();
